import { useSelector } from 'react-redux';
import {
  currentProject,
  selectCurrentBuilding,
  projectColors as selectProjectColors,
} from 'spw-redux/modules/projects/selectors';
import React, {
  createContext,
  FC,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useNavigation } from 'spw-components/draggable-navigation-menu/use-navigation';
import { useSpaceTypeCategories } from 'spw-shared/hooks/use-space-type-categories';
import _ from 'lodash';
import { removeTypeNameFromObject } from 'spw-shared/utils';
import {
  SummaryContainer,
  SummaryMessage,
} from 'spw-programming-projects/program-summary/components/styles';
import {
  AMENITY_SPACES_CATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MEETING_SPACES_CATEGORY,
  SUPPORT_SPACES_CATEGORY,
} from 'spw-constants';
import {
  UserRoleInProject,
  ProgramSummaryCardIcon,
} from 'spw-redux/modules/projects/types';
import ImageSelectorPopover from 'spw-components/project-details-tabs/program-summary/shared/image-selector-popover';
import { Grid } from '@mui/material';
import {
  EmptyMessageWrapper,
  FillableDiv,
  PaddedGrid,
} from 'spw-components/project-details-tabs/program-summary/esa-single-page-summary/styles';
import { ProjectOverview } from 'spw-esa-projects/program-summary/cards/project-overview';
import { KeyDataPoints } from 'spw-esa-projects/program-summary/cards/key-data-points';
import SpaceTypeAllocationWorkspaceUsfChart from 'spw-components/charts/space-type-allocation-workspace-usf-chart';
import SpaceTypeAllocationUsfDetailChart from 'spw-components/charts/program-summary/esa/space-type-allocation-usf-detail-chart';
import { HeadcountUSFCard } from 'spw-esa-projects/program-summary/cards/headcount-usf-card';
import { SeatCountUSFCard } from 'spw-esa-projects/program-summary/cards/seatcount-usf-card';
import { DeskSharingRatioCard } from 'spw-esa-projects/program-summary/cards/desk-sharing-card';
import { USFBreakdownCard } from 'spw-esa-projects/program-summary/cards/usf-breakdown-card';
import { MeetingSeatRatioCard } from 'spw-esa-projects/program-summary/cards/meeting-seat-ratio-card';
import { ScrollableGrid } from 'spw-shared/globalComponents';
import { WorkspaceUsfMix } from 'spw-esa-projects/program-summary/cards/workspace-usf-mix';
import { IndividualWorkspaceSeatMix } from 'spw-esa-projects/program-summary/cards/individual-workspace-seat-mix';
import { CategorySummaryRow } from 'spw-esa-projects/program-summary/cards/category-summary-row';
import { CATEGORY_SUMMARY_ROWS_TEXTS_NEW } from 'spw-components/project-details-tabs/program-summary/esa-single-page-summary/constants';
import DraggableNavigationMenu from 'spw-components/draggable-navigation-menu';
import {
  ISpacesBreakDown,
  UpdateSpacesByTypeParams,
} from 'spw-redux/modules/program-summary/types';
import {
  selectUsfTotal,
  selectKeyData,
  selectBreakdown,
  selectAltWorkpointsBreakdown,
} from 'spw-redux/modules/program-summary/esa/selectors';
import {
  Category,
  IProjectSpaceType,
  Subcategory,
} from 'spw-redux/modules/space_types/types';
import { EmptyListBoxWrapper } from 'spw-components/project-details-tabs/program-summary/shared/styles';
import { EmptyListMessage } from 'spw-components/project-details-tabs/program-summary/shared/empty-list-message';
import { selectProjectSettings } from 'spw-redux/modules/settings/selector';
import { selectProjectSpaceTypesAsMapBySpaceTypeId } from 'spw-redux/modules/space_types/selectors';
import { ESAProgramSummaryCardHeader } from 'spw-components/downloads/image-header';
import {
  useVisibleCards,
  VisibleCards,
} from 'spw-esa-projects/program-summary/hooks';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import IconSelectorPopover from 'spw-esa-projects/program-summary/icon-selector-popover';
import WorkpointsCard from './cards/workpoints';
import SeatCountUSFWithWorkpoints from './cards/seatcount-usf-with-workpoints';
import DeskSharingWithWorkpoints from './cards/desk-sharing-with-workpoints';
import AlternativeWorkpointsBreakdownCard from './cards/alternative-workpoints-breakdown';

const getMarginBottom = (breakdown: ISpacesBreakDown): string => {
  const lastVisibleCategory = [
    SUPPORT_SPACES_CATEGORY,
    AMENITY_SPACES_CATEGORY,
    MEETING_SPACES_CATEGORY,
    INDIVIDUAL_WORKSPACES_CATEGORY,
  ].find((category) => breakdown[category]?.length);

  const rowsInLastVisibleCategory = breakdown[lastVisibleCategory]?.length;

  const EMPTY_CARD_HEIGHT = 183;
  const PER_ROW_HEIGHT = 64;
  let marginBottom = rowsInLastVisibleCategory
    ? window.innerHeight -
      EMPTY_CARD_HEIGHT -
      PER_ROW_HEIGHT * rowsInLastVisibleCategory
    : 0;

  marginBottom = marginBottom > 0 ? marginBottom : 0;

  return `${marginBottom}px`;
};

type EnabledCategoriesMap = {
  [key in Category | Subcategory]: boolean;
};

const chartStyle = {
  height: '100%',
  flexDirection: 'column',
  justifyContent: 'space-between',
};

const EmptyMessage: FC = () => (
  <EmptyMessageWrapper>
    <EmptyListBoxWrapper>
      <EmptyListMessage />
    </EmptyListBoxWrapper>
  </EmptyMessageWrapper>
);

type Props = {
  isRenderingForExport?: boolean;
  showRsfData?: boolean;
  clientFacingNamesAreVisible?: boolean;
  onUpdateAvg?: (payload: UpdateSpacesByTypeParams) => void;
};

export const ProgramSummaryCardsContext = createContext<{
  showRsf: boolean;
  isRenderingForExport: boolean;
  visibleCards: VisibleCards;
  onClickCardIcon?: (
    event: React.MouseEvent<HTMLDivElement>,
    cardIcon: ProgramSummaryCardIcon
  ) => void;
  selectedCardIcon?: ProgramSummaryCardIcon;
}>({
  showRsf: false,
  isRenderingForExport: false,
  visibleCards: null,
  onClickCardIcon: null,
  selectedCardIcon: null,
});

const SummaryCards = ({
  isRenderingForExport = false,
  showRsfData,
  onUpdateAvg,
  clientFacingNamesAreVisible,
}: Props) => {
  const { getUnit } = useUnitConversion();
  const project = useSelector(currentProject);
  const building = useSelector(selectCurrentBuilding);
  const projectColors = useSelector(selectProjectColors);
  const usfTotal = useSelector(selectUsfTotal);
  const keyData = useSelector(selectKeyData);
  const breakdown = useSelector(selectBreakdown);
  const projectSettings = useSelector(selectProjectSettings);
  const altWorkpointsBreakdown = useSelector(selectAltWorkpointsBreakdown);
  const projectSpaceTypes: { [key: number]: IProjectSpaceType } = useSelector(
    selectProjectSpaceTypesAsMapBySpaceTypeId
  );
  const [marginBottom, setMarginBottom] = useState(getMarginBottom(breakdown));
  const [
    imageSelectorAnchor,
    setImageSelectorAnchor,
  ] = useState<Element | null>(null);
  const [iconSelectorAnchorEl, setIconSelectorAnchorEl] = useState<
    HTMLDivElement
  >();
  const [cardIcon, setCardIcon] = useState<ProgramSummaryCardIcon>();

  const onClickCardIcon = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement>,
      selectedCardIcon: ProgramSummaryCardIcon
    ) => {
      setIconSelectorAnchorEl(e.currentTarget);
      setCardIcon(selectedCardIcon);
    },
    []
  );

  const handleWindowResize = useCallback(() => {
    setMarginBottom(getMarginBottom(breakdown));
  }, [setMarginBottom, breakdown]);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [handleWindowResize]);

  useEffect(() => {
    setMarginBottom(getMarginBottom(breakdown));
  }, [setMarginBottom, breakdown]);

  const { spaceTypeCategories } = project;
  const { programSummaryRef, refMap } = useNavigation(spaceTypeCategories);
  const spaceTypeCategoriesByCategory = useSpaceTypeCategories(
    spaceTypeCategories
  );
  const {
    individualWorkspacesCategory,
    meetingSpacesCategory,
    amenitySpacesCategory,
    supportSpacesCategory,
  } = spaceTypeCategoriesByCategory;

  const { circulation, total, subtotal, circulationPercentage } = usfTotal;

  const _keyData = _(removeTypeNameFromObject(keyData));
  const viewOnly = project?.currentUserRole === UserRoleInProject.viewer;
  const { visibleCards, setVisibleCards } = useVisibleCards();

  const showContent = building && _keyData.values().some();

  const enabledCategories: EnabledCategoriesMap = [
    SUPPORT_SPACES_CATEGORY,
    AMENITY_SPACES_CATEGORY,
    MEETING_SPACES_CATEGORY,
    INDIVIDUAL_WORKSPACES_CATEGORY,
  ].reduce(
    (map, category) => ({ ...map, [category]: !!breakdown[category].length }),
    {}
  ) as EnabledCategoriesMap;

  if (_keyData.values().every((x) => x === 0)) {
    return (
      <SummaryContainer
        style={{ justifyContent: 'center' }}
        container
        key="summary"
        data-testid="esa_summary_container"
      >
        <SummaryMessage>
          Program Summary will appear when a space has been added to the floor
        </SummaryMessage>
      </SummaryContainer>
    );
  }

  return (
    <SummaryContainer
      ref={programSummaryRef}
      container
      key="summary"
      data-testid="esa_summary_container"
      style={{
        position: 'relative',
        padding: isRenderingForExport ? '0px' : '16px',
        marginBottom:
          showContent && !isRenderingForExport ? marginBottom : '0px',
      }}
    >
      {showContent && (
        <ProgramSummaryCardsContext.Provider
          value={{
            showRsf: showRsfData,
            isRenderingForExport,
            visibleCards,
            onClickCardIcon,
            selectedCardIcon: cardIcon,
          }}
        >
          {isRenderingForExport && (
            <ESAProgramSummaryCardHeader
              setVisibleCards={setVisibleCards}
              styles={{
                paddingLeft: 19,
                width: '100%',
                position: 'relative',
              }}
            />
          )}
          {!viewOnly && (
            <ImageSelectorPopover
              project={project}
              anchorEl={imageSelectorAnchor}
              onClose={() => setImageSelectorAnchor(null)}
            />
          )}
          {!viewOnly && (
            <IconSelectorPopover
              anchorEl={iconSelectorAnchorEl}
              cardIcon={cardIcon}
              onClose={() => {
                setIconSelectorAnchorEl(null);
                setCardIcon(null);
              }}
            />
          )}
          <Grid container>
            {(!isRenderingForExport ||
              visibleCards.projectOverview ||
              visibleCards.programSummaryReport) && (
              <PaddedGrid item xs={4} style={{ minHeight: 330 }}>
                <FillableDiv>
                  <ProjectOverview project={project} />
                </FillableDiv>
              </PaddedGrid>
            )}

            {(!isRenderingForExport ||
              visibleCards.keyDataPoints ||
              visibleCards.programSummaryReport) && (
              <PaddedGrid item xs={2} style={{ minHeight: 330 }}>
                <FillableDiv>
                  <KeyDataPoints usfTotal={usfTotal} keyData={keyData} />
                </FillableDiv>
              </PaddedGrid>
            )}

            {(!isRenderingForExport ||
              visibleCards.spaceTypeAllocationIndividualWorkspacesUsf ||
              visibleCards.programSummaryReport) && (
              <PaddedGrid item xs={3} style={{ minHeight: 330 }}>
                <SpaceTypeAllocationWorkspaceUsfChart
                  breakdown={breakdown}
                  chartStyle={{
                    ...chartStyle,
                    height: '100%',
                    justifyContent: 'start',
                  }}
                  showNamesInTable={false}
                  category={individualWorkspacesCategory}
                  style={{ height: '100%' }}
                />
              </PaddedGrid>
            )}

            {(!isRenderingForExport ||
              visibleCards.spaceTypeAllocationSubtotalUsf ||
              visibleCards.programSummaryReport) && (
              <PaddedGrid item xs={3} style={{ minHeight: 330 }}>
                <SpaceTypeAllocationUsfDetailChart
                  style={{
                    ...chartStyle,
                  }}
                  showNamesInTable={false}
                  spaceTypesUsf={{
                    supportSpaces: _.sumBy(breakdown.supportSpaces, 'usf'),
                    amenitySpaces: _.sumBy(breakdown.amenitySpaces, 'usf'),
                    individualWorkspaces: _.sumBy(
                      breakdown.individualWorkspaces,
                      'usf'
                    ),
                    meetingSpaces: _.sumBy(breakdown.meetingSpaces, 'usf'),
                  }}
                  projectSettings={projectSettings}
                  spaceTypeCategories={spaceTypeCategories}
                />
              </PaddedGrid>
            )}
          </Grid>
          <Grid container>
            {(!isRenderingForExport ||
              visibleCards.headcountUsf ||
              visibleCards.programSummaryReport) && (
              <PaddedGrid
                item
                xs={2}
                className="mr-16"
                style={{ minHeight: 158 }}
              >
                <HeadcountUSFCard />
              </PaddedGrid>
            )}

            {(!isRenderingForExport ||
              visibleCards.seatCountUsf ||
              visibleCards.programSummaryReport) && (
              <PaddedGrid
                item
                xs={2}
                className="mr-16"
                style={{ minHeight: 158 }}
              >
                <SeatCountUSFCard
                  seatCountUsf={keyData.workseatDensity}
                  data-testid="esa_summary_seat_count_usf"
                />
              </PaddedGrid>
            )}

            {(!isRenderingForExport ||
              visibleCards.deskSharingRatio ||
              visibleCards.programSummaryReport) && (
              <PaddedGrid
                item
                xs={2}
                className="mr-16"
                style={{ minHeight: 158 }}
              >
                <DeskSharingRatioCard data-testid="esa_summary_desk_sharing_ratio" />
              </PaddedGrid>
            )}

            {(!isRenderingForExport ||
              visibleCards.usfBreakdown ||
              visibleCards.programSummaryReport) && (
              <PaddedGrid item xs={3} style={{ minHeight: 158 }}>
                <USFBreakdownCard
                  subtotal={subtotal}
                  circulationPercent={circulationPercentage}
                  circulation={circulation}
                  total={total}
                />
              </PaddedGrid>
            )}

            {(!isRenderingForExport ||
              visibleCards.meetingSeatRatio ||
              visibleCards.programSummaryReport) && (
              <PaddedGrid item xs={3} style={{ minHeight: 158 }}>
                <MeetingSeatRatioCard
                  meetingSeats={keyData.meetingSeats}
                  workSeats={keyData.workseats}
                  relation={keyData.meetingSeats / keyData.workseats}
                />
              </PaddedGrid>
            )}
          </Grid>
          {altWorkpointsBreakdown?.all.count > 0 ? (
            <Grid container>
              {(!isRenderingForExport ||
                visibleCards.workpoints ||
                visibleCards.programSummaryReport) && (
                <PaddedGrid item xs={2} style={{ minHeight: 220 }}>
                  <WorkpointsCard />
                </PaddedGrid>
              )}

              {(!isRenderingForExport ||
                visibleCards.seatCountUsfWithWorkpoints ||
                visibleCards.programSummaryReport) && (
                <PaddedGrid item xs={2} style={{ minHeight: 220 }}>
                  <SeatCountUSFWithWorkpoints />
                </PaddedGrid>
              )}

              {(!isRenderingForExport ||
                visibleCards.deskSharingWithWorkpoints ||
                visibleCards.programSummaryReport) && (
                <PaddedGrid item xs={2} style={{ minHeight: 220 }}>
                  <DeskSharingWithWorkpoints />
                </PaddedGrid>
              )}

              {(!isRenderingForExport ||
                visibleCards.alternativeWorkpointsBreakdown ||
                visibleCards.programSummaryReport) && (
                <PaddedGrid item xs={6} style={{ minHeight: 220 }}>
                  <AlternativeWorkpointsBreakdownCard
                    spaceTypeCategoriesByCategory={
                      spaceTypeCategoriesByCategory
                    }
                  />
                </PaddedGrid>
              )}
            </Grid>
          ) : null}
          <ScrollableGrid
            container
            ref={refMap[individualWorkspacesCategory?.id]}
            $scrollMarginTop={132}
          >
            {(!isRenderingForExport ||
              visibleCards.categoryUsfAndSeatMix[INDIVIDUAL_WORKSPACES_CATEGORY]
                .usfAndSeatMix ||
              visibleCards.categoryUsfAndSeatMix[INDIVIDUAL_WORKSPACES_CATEGORY]
                .usfMix) && (
              <PaddedGrid
                item
                xs={6}
                className="mr-16"
                data-testid="esa_summary_individual_workspaces_workspace_usf_mix"
              >
                <WorkspaceUsfMix
                  spaceTypeCategory={individualWorkspacesCategory}
                  viewOnly={viewOnly}
                  projectType={project.type}
                  workspacesBreakdown={breakdown.individualWorkspaces}
                  projectColors={projectColors}
                  title={`${individualWorkspacesCategory?.name} ${getUnit(
                    'area'
                  )} MIX`}
                  projectSpaceTypeCategories={project.spaceTypeCategories}
                  projectSpaceTypes={projectSpaceTypes}
                  onClickSpaceIcon={(e) => {
                    setImageSelectorAnchor(e);
                  }}
                  overrideContent={
                    !enabledCategories[INDIVIDUAL_WORKSPACES_CATEGORY] ? (
                      <EmptyMessage />
                    ) : undefined
                  }
                  onUpdateAvgUsf={onUpdateAvg}
                  clientFacingNamesAreVisible={clientFacingNamesAreVisible}
                />
              </PaddedGrid>
            )}

            {(!isRenderingForExport ||
              visibleCards.categoryUsfAndSeatMix[INDIVIDUAL_WORKSPACES_CATEGORY]
                .usfAndSeatMix ||
              visibleCards.categoryUsfAndSeatMix[INDIVIDUAL_WORKSPACES_CATEGORY]
                .seatMix) && (
              <PaddedGrid
                item
                xs={6}
                data-testid="esa_summary_individual_workspaces_workspace_seat_mix"
              >
                <IndividualWorkspaceSeatMix
                  viewOnly={viewOnly}
                  individualWorkspacesBreakdown={breakdown.individualWorkspaces}
                  projectColors={projectColors}
                  title={`${individualWorkspacesCategory?.name} Seat Mix`}
                  onClickSpaceIcon={(e) => {
                    setImageSelectorAnchor(e);
                  }}
                  overrideContent={
                    !enabledCategories[INDIVIDUAL_WORKSPACES_CATEGORY] ? (
                      <EmptyMessage />
                    ) : undefined
                  }
                  projectSpaceTypeCategories={project.spaceTypeCategories}
                  projectSpaceTypes={projectSpaceTypes}
                  spaceTypeCategory={individualWorkspacesCategory}
                  onUpdateAvgSeatCount={onUpdateAvg}
                  clientFacingNamesAreVisible={clientFacingNamesAreVisible}
                />
              </PaddedGrid>
            )}
          </ScrollableGrid>
          <>
            {[
              meetingSpacesCategory,
              amenitySpacesCategory,
              supportSpacesCategory,
            ].map((spaceTypeCategory) => {
              const { category } = spaceTypeCategory.defaultSpaceTypeCategory;

              return (
                enabledCategories[category] && (
                  <CategorySummaryRow
                    key={spaceTypeCategory.id}
                    innerRef={refMap[spaceTypeCategory.id]}
                    viewOnly={viewOnly}
                    currentProject={project}
                    categorySpacesBreakdown={breakdown[category]}
                    onClickSpaceIcon={(e) => {
                      setImageSelectorAnchor(e);
                    }}
                    projectColors={projectColors}
                    spaceTypeCategory={spaceTypeCategory}
                    projectSpaceTypeCategories={project.spaceTypeCategories}
                    projectSpaceTypes={projectSpaceTypes}
                    onUpdateAvg={onUpdateAvg}
                    {...CATEGORY_SUMMARY_ROWS_TEXTS_NEW[category]}
                    clientFacingNamesAreVisible={clientFacingNamesAreVisible}
                  />
                )
              );
            })}
          </>
          {!isRenderingForExport && !!programSummaryRef.current && (
            <DraggableNavigationMenu
              refMap={refMap}
              parent={programSummaryRef}
              spaceTypeCategories={spaceTypeCategories}
              getNavigationButtonProps={(spaceTypeCategory, scrollTo) => {
                const { category } = spaceTypeCategory.defaultSpaceTypeCategory;

                const enableCategory = enabledCategories[category];

                return {
                  className: !enableCategory ? 'disabled' : '',
                  onClick: () => {
                    if (enableCategory) {
                      scrollTo(spaceTypeCategory.id);
                    }
                  },
                };
              }}
            />
          )}
        </ProgramSummaryCardsContext.Provider>
      )}
    </SummaryContainer>
  );
};

export default SummaryCards;
